import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { InsufficientCreditsModal } from '@src/presentation/components/InsufficientCreditsModal';
import { insufficientCreditsEmitter } from '@src/shared/services/insufficient-credits-emitter';

interface InsufficientCreditsData {
    message?: string;
    topUpUrl?: string;
}

interface InsufficientCreditsContextType {
    showInsufficientCreditsModal: (data?: InsufficientCreditsData) => void;
}

const InsufficientCreditsContext = createContext<InsufficientCreditsContextType | undefined>(undefined);

interface InsufficientCreditsProviderProps {
    children: ReactNode;
}

export function InsufficientCreditsProvider({ children }: InsufficientCreditsProviderProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [creditsData, setCreditsData] = useState<InsufficientCreditsData>({});

    const showInsufficientCreditsModal = (data?: InsufficientCreditsData) => {
        setCreditsData(data || {});
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
        setCreditsData({});
    };

    // Listen to global 402 events from API client
    useEffect(() => {
        const unsubscribe = insufficientCreditsEmitter.subscribe((data) => {
            showInsufficientCreditsModal(data);
        });

        return unsubscribe;
    }, []);

    return (
        <InsufficientCreditsContext.Provider value={{ showInsufficientCreditsModal }}>
            {children}
            <InsufficientCreditsModal
                isOpen={isOpen}
                onClose={handleClose}
                message={creditsData.message}
                topUpUrl={creditsData.topUpUrl}
            />
        </InsufficientCreditsContext.Provider>
    );
}

export function useInsufficientCredits() {
    const context = useContext(InsufficientCreditsContext);
    if (context === undefined) {
        throw new Error('useInsufficientCredits must be used within an InsufficientCreditsProvider');
    }
    return context;
}
